import type { Control } from 'react-hook-form'
import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { Button } from '~/components/ui/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_REQUEST_TRIAL } from '~/gql/trial/trial'
import SignupTermsField from './SignupTermsField'

type Values = { firstName: string, lastName: string, email: string, orgName: string, phone?: string, jobTitle?: string, acceptTerms: boolean }

type FieldProps = {
  control: Control<Values>
  name: Exclude<keyof Values, 'acceptTerms'>
  label: string
  autoComplete: string
  type?: 'text' | 'email' | 'tel'
  required?: boolean
  placeholder?: string
  /** Aide sous le champ ; `FormControl` la relie au champ par `aria-describedby`, comme l'erreur. */
  hint?: string
}

function SignupField({ control, name, label, autoComplete, type = 'text', required, placeholder, hint }: FieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="grid gap-2">
            <Label htmlFor={name} required={required}>{label}</Label>
            <FormControl>
              <Input id={name} aria-required={required} autoComplete={autoComplete} type={type} placeholder={placeholder} {...field} />
            </FormControl>
            {hint && <FormDescription className="text-xs">{hint}</FormDescription>}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

type Props = { onSent: (email: string) => void, onBack: () => void }

/**
 * Le formulaire qualifié de la demande d'essai (#155). L'API répond toujours
 * pareil pour ne pas devenir un oracle d'existence : seul un domaine grand
 * public est refusé explicitement (adresse pro exigée).
 */
function SignupForm({ onSent, onBack }: Props) {
  const { t } = useTranslation()
  const required = z.string().min(1, { message: t('input.required.message') })
  const schema = z.object({
    firstName: required,
    lastName: required,
    email: z.string().email({ message: t('input.email.message') }),
    orgName: required,
    phone: z.string().optional(),
    jobTitle: z.string().optional(),
    acceptTerms: z.boolean().refine(accepted => accepted, { message: t('signup.form.terms.required') }),
  })
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: '', lastName: '', email: '', orgName: '', phone: '', jobTitle: '', acceptTerms: false },
  })
  const [requestTrial, { loading }] = useMutation(GQL_REQUEST_TRIAL)

  const onSubmit = async (values: Values) => {
    try {
      await requestTrial({
        variables: {
          input: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            orgName: values.orgName,
            phone: values.phone?.trim() || undefined,
            jobTitle: values.jobTitle?.trim() || undefined,
            acceptTerms: values.acceptTerms,
          },
        },
      })
      onSent(values.email)
    } catch (err) {
      if (String(err).includes('PUBLIC_EMAIL_DOMAIN')) form.setError('email', { message: t('signup.error.publicEmail') })
      else toast.error(t('signup.error.generic'))
    }
  }

  const { control } = form
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <p className="text-muted-foreground">{t('signup.form.subtitle')}</p>
        <RequiredFieldsHint />

        <div className="grid gap-6 sm:grid-cols-2">
          <SignupField control={control} name="firstName" label={t('common.firstname')} autoComplete="given-name" required />
          <SignupField control={control} name="lastName" label={t('common.lastname')} autoComplete="family-name" required />
        </div>
        <SignupField control={control} name="email" type="email" label={t('signup.form.email')} autoComplete="email" placeholder="prenom@organisme.fr" hint={t('signup.form.email.hint')} required />
        <SignupField control={control} name="orgName" label={t('signup.form.orgName')} autoComplete="organization" required />
        <div className="grid gap-6 sm:grid-cols-2">
          <SignupField control={control} name="jobTitle" label={t('signup.form.jobTitle')} autoComplete="organization-title" />
          <SignupField control={control} name="phone" type="tel" label={t('signup.form.phone')} autoComplete="tel" />
        </div>
        <SignupTermsField control={control} name="acceptTerms" />

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onBack} className="h-12 shrink-0 rounded-full" aria-label={t('common.back')}>
            <ArrowLeft className="size-4" aria-hidden />
          </Button>
          <Button type="submit" disabled={loading} className="h-12 flex-1 rounded-full">
            {loading && <Loader className="mr-2 size-4 animate-spin" aria-hidden />}
            {t('signup.form.submit')}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default SignupForm
